/**
 * Studio F Agents - Widget entry point.
 *
 * DEUX modes d'intégration, exposés par les data attributes du <script> :
 *
 *   1. Embed prod (chez un client) — par slug client :
 *      <script async
 *              src="https://agents.studiof.pro/widget.js"
 *              data-agent="cabinet-dupuis"></script>
 *
 *   2. Démo publique (page /demo/[agentId] sur agents.studiof.pro) — par
 *      UUID agent, chat ouvert par défaut :
 *      <script async
 *              src="https://agents.studiof.pro/widget.js"
 *              data-agent-id="3f2504e0-..."
 *              data-demo="true"
 *              data-open="true"></script>
 *
 * `data-agent-id` prend la priorité sur `data-agent` si les deux sont
 * présents (ne devrait pas arriver, mais on tranche).
 *
 * Le script :
 * 1. Lit les data-* attributes du <script> qui le charge
 * 2. Détecte l'URL de base de l'API depuis le src du <script>
 * 3. Monte l'app Preact dans un conteneur isolé du DOM hôte
 */

import { render } from 'preact';
import { App } from './components/App';
import { type WidgetMode, setApiBase } from './lib/api';
import { isWidgetPageAllowed, watchPageNavigation } from './lib/page-visibility';
import widgetStyles from './styles/widget.css?inline';

function injectStyles() {
  if (document.getElementById('studiof-agents-styles')) return;
  const style = document.createElement('style');
  style.id = 'studiof-agents-styles';
  style.textContent = widgetStyles;
  document.head.appendChild(style);
}

function resolveMode(script: HTMLScriptElement | null): WidgetMode | null {
  const agentId = script?.dataset.agentId;
  // data-preview="true" + data-agent-id : mode admin live preview. La query
  // string du window est transmise au server pour overrider les champs
  // branding (design / layout / couleurs / offsets) sans modifier l'agent en DB.
  if (agentId && script?.dataset.preview === 'true') {
    return {
      kind: 'preview',
      agentId,
      searchString: typeof window !== 'undefined' ? window.location.search : '',
    };
  }
  if (agentId) {
    return { kind: 'demo', agentId };
  }
  const slug = script?.dataset.agent;
  if (slug) {
    return { kind: 'prod', slug };
  }
  return null;
}

function init() {
  injectStyles();
  const currentScript = (document.currentScript ??
    document.querySelector(
      'script[data-agent], script[data-agent-id]',
    )) as HTMLScriptElement | null;

  const mode = resolveMode(currentScript);

  if (!mode) {
    console.error(
      '[StudioFAgents] Missing data-agent or data-agent-id on <script>. Aborting widget load.',
    );
    return;
  }

  if (
    mode.kind === 'prod' &&
    mode.slug === 'holiclay' &&
    document.getElementById('studiof-agents-root')
  )
    return;

  // Détermine l'URL de l'API depuis le src du script
  // ex: https://agents.studiof.pro/widget.js → https://agents.studiof.pro
  const scriptSrc = currentScript?.src;
  if (scriptSrc) {
    try {
      const url = new URL(scriptSrc);
      setApiBase(`${url.protocol}//${url.host}`);
    } catch {
      console.error('[StudioFAgents] Invalid script src, cannot determine API base.');
      return;
    }
  } else {
    // Fallback dev : si le script est local (Vite dev), on suppose que l'admin
    // tourne sur le même host avec un port différent.
    setApiBase(window.location.origin);
  }

  // openByDefault : pour la démo publique, le chat doit s'ouvrir tout seul.
  // En embed prod c'est false (le visiteur clique sur la bulle pour ouvrir).
  const openByDefault = currentScript?.dataset.open === 'true';

  // data-target="#selector" : permet d'integrer le widget en layout Inline
  // dans un div hote precis de la page (vs append au body, comme un widget
  // flottant). Le user doit avoir ce div present a l'endroit ou il veut le
  // chat embarque. Si data-target est present mais l'element introuvable,
  // on warn et on tombe sur le mount body classique. Les autres layouts
  // (bubble, drawer, modal, sheet) ignorent ce data-target.
  const targetSelector = currentScript?.dataset.target;
  let mountTarget: HTMLElement | null = null;
  if (targetSelector) {
    mountTarget = document.querySelector<HTMLElement>(targetSelector);
    if (!mountTarget) {
      console.warn(
        `[StudioFAgents] data-target="${targetSelector}" introuvable. Fallback : mount en bas de <body>.`,
      );
    }
  }

  // Crée un conteneur dédié pour éviter de polluer le DOM hôte.
  // all:initial reset les styles herites du parent (font-size, color, etc.).
  const container = document.createElement('div');
  container.id = 'studiof-agents-root';
  container.style.all = 'initial';
  (mountTarget ?? document.body).appendChild(container);

  if (mode.kind === 'prod' && mode.slug === 'holiclay') {
    let mounted = false;
    const refresh = () => {
      const visible = isWidgetPageAllowed(mode.slug, window.location.pathname);
      container.hidden = !visible;
      // all:initial overrides the browser's default [hidden] display rule.
      container.style.display = visible ? 'contents' : 'none';
      if (visible === mounted) return;
      render(visible ? <App mode={mode} openByDefault={openByDefault} /> : null, container);
      mounted = visible;
    };
    refresh();
    watchPageNavigation(window, refresh);
  } else {
    render(<App mode={mode} openByDefault={openByDefault} />, container);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
